/**
 * 流向分组规则（页面和后端共用，保证「表格里的分组」和「点开的明细」口径一致）。
 * 规则照搬产品门户原型 lib/flow.ts，不要改写。
 *
 *  - 发干线（目的地是别的 HUB，或码里没有仓库段）：每个目的地一行，只显示一段码总数，不再拆二段、三段。
 *      例：LAX 仓里的 ORD-CVG-B002 → 「ORD」；HOU → 「HOU」
 *  - 发支线（一段码就是本仓，如 LAX 的 LA、EWR 的 EWR，后面跟 仓库-字母...）：页面上合并为「发支线」总数一行，
 *    其下按二段(warehouse)，再下按「二段 - 三段字母」三层展开。
 *      例：LA-LADC-H083 → 二段 'LADC'，字母级 'LADC-H'；EWR-MHT-A-03 → 'MHT' / 'MHT-A'
 *    只有二段、没有三段字母的支线（如 LA-LADC）归在二段下的「未分字母」。
 */

export interface FlowKey {
    /** 表格里的一级行：干线 = 一段码，支线 = 二段 */
    group: string;
    /** 支线才有的二级行（二段-三段字母），没有字母为 ''；干线为 null */
    child: string | null;
    trunk: boolean;
}

/**
 * 两个兜底分组的取值。它们会作为 g1 传给明细接口，属于与后端约定的口径值，不能随语言变化；
 * 页面展示时由 buildFlowTree 的 labels 参数替换成当前语言的文案。
 */
export const UNKNOWN_FLOW = '未知流向';
export const NO_WAREHOUSE = '未分仓';

/** 各仓在三段码里的本地代号（LAX HUB 在码里叫 LA） */
const LOCAL_CODES: Record<string, string[]> = {
    LAX: ['LA', 'LAX'],
    EWR: ['EWR']
};

export function flowKey(hub: string, flow: string | null | undefined): FlowKey {
    const f = (flow ?? '').trim();
    if (!f) return { group: UNKNOWN_FLOW, child: null, trunk: true };
    const segs = f.split('-');
    const local = LOCAL_CODES[hub] ?? [hub];
    if (!local.includes(segs[0])) return { group: segs[0] || f, child: null, trunk: true };
    // 一段码是本仓、但后面没有仓库段（如只有 'LA'）：属于发支线，只是还没分到具体 warehouse
    if (segs.length < 2) return { group: NO_WAREHOUSE, child: '', trunk: false };
    const second = segs[1];
    const letter = (segs[2] ?? '').match(/^[A-Za-z]+/)?.[0];
    return { group: second, child: letter ? `${second}-${letter}` : '', trunk: false };
}

/** 支线行在表格里的显示前缀（LAX 仓叫 LA，如 LA-COI、LA-COI-A；EWR 仓叫 EWR） */
export function branchPrefix(hub: string): string {
    return (LOCAL_CODES[hub] ?? [hub])[0];
}
